import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useDispatch, useMappedState } from 'redux-react-hook';
import styled from 'styled-components';
import _size from 'lodash/size';
import _get from 'lodash/get';
import { updateDateAnswer, setDateAnswerId } from 'redux/actions/consultation/answer';
import { ConstructedDateInput as DateInput } from 'styles/blocks/dateInput';
import { ColumnDiv } from 'styles/elements/div';
import { STRINGS } from 'config';
import { calculateExampleDate, splitYear } from 'common/dates';
import { useAutoFocus } from 'common/customHooks';
import { HelpLabel } from 'styles/elements/label';
import { consultationSelector } from 'redux/selectors/consultation';
import { NextButton } from '../nextButton';

const HelpLabelLower = styled(HelpLabel)`
  padding-top: 10px;
`;

const HelpLabelWrapper = ({ exampleDate, isLabelAboveInput, children }) => {
  if (isLabelAboveInput) {
    return (
      <ColumnDiv>
        {exampleDate && <HelpLabel>For example: {exampleDate}</HelpLabel>}
        {children}
      </ColumnDiv>
    );
  }
  return (
    <ColumnDiv>
      {children}
      {exampleDate && <HelpLabelLower>For example: {exampleDate}</HelpLabelLower>}
    </ColumnDiv>
  );
};

export function DateField({ isInvalid, isMounted, onClickNextQuestion }) {
  const mapState = useCallback(state => consultationSelector(state), []);
  const { dateAnswer, question, product, inputsReadOnly } = useMappedState(mapState);

  const [isLabelAboveInput] = useState(() => !(product === STRINGS.PRODUCT.E_TRIAGE));

  // actions to update value
  const dispatch = useDispatch();
  useEffect(() => {
    if (question.type === STRINGS.QUESTION_TYPE.DATE) {
      dispatch(setDateAnswerId(_get(question.answers[0], 'answerId', null)));
    }
  }, [dispatch, question]);
  const [date, setDate] = useState(dateAnswer);
  const [isOptional] = useState(question.optional);

  const isNextButtonDisabled = !isOptional && (!(date.d && date.m && date.y) || _size(date.y) < 4);

  const updateDate = useCallback((newDate, dateUnit) => {
    if (dateUnit === 'DAY') {
      setDate({ ...date, d: newDate });
    } else if (dateUnit === 'MONTH') {
      setDate({ ...date, m: newDate });
    } else if (dateUnit === 'YEAR') {
      setDate({ ...date, y: newDate });
    }
    dispatch(updateDateAnswer(newDate, dateUnit));
  }, [dispatch, date]);

  const [exampleDate] = useState(calculateExampleDate(question.minDate, question.maxDate));

  const inputRef = useRef();
  useAutoFocus(true, isMounted, inputRef);

  return (
    <ColumnDiv>
      <HelpLabelWrapper exampleDate={exampleDate} isLabelAboveInput={isLabelAboveInput}>
        <DateInput
          day={date.d}
          month={date.m}
          year={date.y}
          onUpdateDate={updateDate}
          isInvalid={isInvalid}
          inputRef={inputRef}
          minYear={splitYear(question.minDate)}
          maxYear={splitYear(question.maxDate)}
          isReadOnly={inputsReadOnly}
        />
      </HelpLabelWrapper>
      <NextButton dataAutomation={`nextButton-${question.questionId}`} onClick={onClickNextQuestion} isDisabled={isNextButtonDisabled} />
    </ColumnDiv>
  );
}
